import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { SelectChangeEvent, Typography } from '@mui/material';
import { FC, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import CustomButton from 'components/CustomButton';
import DeliveryModal from 'containers/PosOrderDetailsPage/DeliveryModal';
import DiscountModal from 'containers/PosOrderDetailsPage/DiscountModal';
import PickUpModal from 'containers/PosOrderDetailsPage/PickupModal';
import SaleTypeModal from 'containers/PosOrderDetailsPage/SaleTypeModal';

import { DeliveryMethod } from 'constants/enums';
import { POS_ORDER_DELIVERY_METHODS_OPTIONS } from 'constants/options';
import { POSOrderDetails } from 'types/pos.interface';

import {
  CategoryHeader,
  CustomBox,
  SmallCustomDropdown,
} from './index.styled';

interface CategoryHeaderBarProps {
  order: POSOrderDetails;
  hasMultipleUseTypes: boolean;
  updateHandler: () => void;
  onSuccessHandler: () => void;
}

enum ModalType {
  SALE_TYPE = 'SALE_TYPE',
  DISCOUNT = 'DISCOUNT',
  PICKUP = 'PICKUP',
  DELIVERY = 'DELIVERY',
}

const CategoryHeaderBar: FC<CategoryHeaderBarProps> = ({
  order,
  hasMultipleUseTypes,
  updateHandler,
  onSuccessHandler,
}) => {
  const [openModal, setOpenModal] = useState<ModalType | null>(null);
  const [selectedDeliveryMethod, setSelectedDeliveryMethod] = useState('');

  const { setValue } = useFormContext();

  const handleOpenDiscountModal = () => {
    setOpenModal(ModalType.DISCOUNT);
  };

  const handleOpenSaleTypeModal = () => {
    if (hasMultipleUseTypes) {
      setOpenModal(ModalType.SALE_TYPE);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(null);
  };

  const selectDeliveryMethod = ({ target: { value } }: SelectChangeEvent<unknown>) => {
    switch (value) {
      case DeliveryMethod.PICKUP:
        setOpenModal(ModalType.PICKUP);
        break;
      case DeliveryMethod.DELIVERY:
        setOpenModal(ModalType.DELIVERY);
        break;
      default:
        setOpenModal(null);
    }
  };

  const handleSubmitPickUpModal = () => {
    setSelectedDeliveryMethod('Pickup');
  };

  const handleChangeSaleType = (useType: string) => {
    setValue('useType', useType);
    updateHandler();
    handleCloseModal();
  };

  return (
    <CategoryHeader>
      <CustomBox>
        <CustomButton
          id="button-startNew"
          variant="outlined"
          size="small"
          fullWidth
        >
          Start new
        </CustomButton>
      </CustomBox>

      <CustomBox>
        <Typography variant="body3">Sale Type</Typography>
        <CustomButton
          id="button-adult"
          variant="contained"
          size="small"
          fullWidth
          onClick={handleOpenSaleTypeModal}
        >
          {order.useType}
        </CustomButton>
      </CustomBox>

      <CustomBox>
        <Typography variant="body3">Order Type</Typography>
        <SmallCustomDropdown
          id="dropdown-type"
          size="small"
          fullWidth
          onChange={selectDeliveryMethod}
          value={selectedDeliveryMethod}
          options={POS_ORDER_DELIVERY_METHODS_OPTIONS}
        />
      </CustomBox>

      <CustomBox>
        <CustomButton
          icon={<LocalOfferIcon />}
          id="button-discount"
          fullWidth
          variant="outlined"
          onClick={handleOpenDiscountModal}
          size="small"
        >
          Discount
        </CustomButton>
      </CustomBox>

      <PickUpModal
        open={openModal === ModalType.PICKUP}
        onClose={handleCloseModal}
        onSubmit={handleSubmitPickUpModal}
      />

      {hasMultipleUseTypes && (
        <SaleTypeModal
          open={openModal === ModalType.SALE_TYPE}
          onClose={handleCloseModal}
          onChange={handleChangeSaleType}
        />
      )}

      <DeliveryModal
        open={openModal === ModalType.DELIVERY}
        onClose={handleCloseModal}
      />

      <DiscountModal
        open={openModal === ModalType.DISCOUNT}
        onClose={handleCloseModal}
        onSuccessHandler={onSuccessHandler}
      />
    </CategoryHeader>
  );
};

export default CategoryHeaderBar;
